// ⌘K command palette: finds a menu entry by name and opens it. It searches the menu tree exactly as
// the rail shows it to this user (useSidebarIdentity + useSidebarMenus: billing flag, professional
// mode, role and custom-role filters), sub-items included, by translated and original label. A
// locked module opens the contact-admin dialog instead of navigating, as in the rail.
//
// Replaces the rail's inline quick search; the trigger carries its tour anchor (`nav-search`).
//
// During a tour (dashboard-welcome's "Cari menu" step invites the user to try it) the palette follows the
// tour: it closes when the tour moves on or ends, or the user clicks the tour's dim (`useTourLayer`), so no
// modal outlives its step. Picking an entry navigates as usual, and TourProvider ends the tour on the route
// change; a locked entry ends the tour first, so the lock dialog does not open under the dim.
import { LockIcon, SearchIcon } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import FeatureLockedModal from '@components/feature-lock/FeatureLockedModal';
import { useSidebarIdentity, useSidebarMenus } from '@components/sidebar/sidebar-v2';
import { MODULE_BY_SIDEBAR_LABEL } from '@constants/modules';
import { useHaptic } from '@utils/haptic';
import { Button } from '@/components/lumen/button';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/lumen/command';
import { useLanguage } from '@/contexts/LanguageContext';
import { useOptionalTour, useTourLayer } from '@/features/onboarding/TourProvider';
import { cn } from '@/lib/utils';

interface PaletteEntry {
  /** Unique cmdk value; also what the default filter scores. */
  value: string;
  label: string;
  link: string;
  icon: ReactNode;
  keywords: string[];
  /** Module label to show in the locked dialog, when the company has the module off. */
  lockedModule?: string;
}

interface PaletteGroup {
  heading: string;
  entries: PaletteEntry[];
}

const isMacPlatform = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

export function CommandPalette() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const hapticMedium = useHaptic('medium');
  const [open, setOpenState] = useState(false);
  const [query, setQuery] = useState('');
  const [lockedModule, setLockedModule] = useState<string | null>(null);
  const tour = useOptionalTour();
  // The lock dialog opens as the palette closes, so it would hand focus back to the palette's input, which
  // is gone by then; send it to the trigger instead.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { userEmail, professionalModeEnabled } = useSidebarIdentity();
  const { filteredMenus, isFeatureEnabled } = useSidebarMenus({ userEmail, professionalModeEnabled });
  const shortcut = isMacPlatform() ? '⌘K' : 'Ctrl K';

  // ⌘K / Ctrl+K opens (and closes) the palette from anywhere the top bar is mounted.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return;
      event.preventDefault();
      setOpenState((previous) => !previous);
      setQuery('');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Mid-tour the palette closes with the tour's step: Next, Back, a click on the dim, the tour ending. That
  // covers Escape too: a running tour takes it at window capture, ends itself and stops the event there
  // (TourProvider), so the dialog's own Escape handler never hears it, and the ending closes the palette.
  const closePalette = useCallback(() => setOpenState(false), []);
  useTourLayer(open, closePalette);

  const groups = useMemo<PaletteGroup[]>(() => {
    const labelOf = (entry: { label: string; i18nKey?: string }) => (entry.i18nKey ? t(entry.i18nKey) : entry.label);
    const links: PaletteEntry[] = [];
    const nested: PaletteGroup[] = [];
    for (const item of filteredMenus) {
      const moduleCode = MODULE_BY_SIDEBAR_LABEL[item.label];
      const locked = moduleCode ? !isFeatureEnabled(moduleCode) : false;
      const groupLabel = labelOf(item);
      if (!item.subLinks) {
        links.push({
          value: `${item.label}|${item.link}`,
          label: groupLabel,
          link: item.link,
          icon: item.icon,
          keywords: [item.label],
          lockedModule: locked ? item.label : undefined,
        });
        continue;
      }
      nested.push({
        heading: groupLabel,
        entries: item.subLinks.map((sub) => ({
          value: `${item.label}|${sub.label}|${sub.link}`,
          label: labelOf(sub),
          link: sub.link,
          icon: sub.icon,
          keywords: [sub.label, item.label, groupLabel],
          lockedModule: locked ? item.label : undefined,
        })),
      });
    }
    return links.length > 0 ? [{ heading: t('lumen.shell.menu'), entries: links }, ...nested] : nested;
  }, [filteredMenus, isFeatureEnabled, t]);

  // Plain substring matching, like the rail's search had: cmdk's own fuzzy scoring alone lets "cnn" match
  // half the menu. A group's label matches all of its entries (they carry it as a keyword). cmdk still
  // ranks what is left.
  const visibleGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((group) => ({
        ...group,
        entries: group.entries.filter((entry) =>
          [entry.label, ...entry.keywords].some((text) => text.toLowerCase().includes(q))
        ),
      }))
      .filter((group) => group.entries.length > 0);
  }, [groups, query]);

  const setOpen = (next: boolean) => {
    setOpenState(next);
    if (next) setQuery('');
  };

  const select = (entry: PaletteEntry) => {
    hapticMedium();
    setOpen(false);
    if (entry.lockedModule) {
      // Mid-tour the lock dialog would open under the tour's dim, out of reach: end the tour first, as a
      // `dismissOnClick` step does.
      tour?.stopTour();
      setLockedModule(entry.lockedModule);
      return;
    }
    navigate(entry.link);
  };

  return (
    <>
      <Button
        ref={triggerRef}
        variant="outline"
        data-tour="nav-search"
        onClick={() => setOpen(true)}
        aria-label={`${t('sidebar.quickSearch')} (${shortcut})`}
        aria-haspopup="dialog"
        // Icon only on phones; a search field from `sm` up.
        className="h-8 w-8 px-0 font-normal text-fg-muted text-13 hover:text-fg-2 has-[>svg]:px-0 sm:w-full sm:max-w-[26rem] sm:justify-start sm:gap-2 sm:px-2.5 sm:has-[>svg]:px-2.5"
      >
        <SearchIcon aria-hidden />
        <span className="hidden flex-1 truncate text-left sm:inline">{t('sidebar.quickSearch')}</span>
        <kbd className="hidden rounded-tag border border-line px-1 font-mono text-[11px] text-fg-muted sm:inline">
          {shortcut}
        </kbd>
      </Button>

      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        // During a tour (the "Cari menu" step lets the user try it) driver.js dims the page and turns
        // pointer events off everywhere but the spotlit trigger; lift the palette above both.
        className="sm:max-w-xl [.driver-active_&]:z-[1000000002] [.driver-active_&]:pointer-events-auto [.driver-active_&_*]:pointer-events-auto"
      >
        <CommandInput placeholder={t('sidebar.quickSearch')} value={query} onValueChange={setQuery} />
        <CommandList className="max-h-[min(26rem,60vh)]">
          <CommandEmpty>{t('sidebar.noMatches')}</CommandEmpty>
          {visibleGroups.map((group) => (
            <CommandGroup key={group.heading} heading={group.heading}>
              {group.entries.map((entry) => (
                <CommandItem
                  key={entry.value}
                  value={entry.value}
                  keywords={[entry.label, ...entry.keywords]}
                  onSelect={() => select(entry)}
                  className={cn(entry.lockedModule && 'text-fg-muted')}
                >
                  {entry.icon}
                  <span className="flex-1 truncate">{entry.label}</span>
                  {entry.lockedModule && (
                    <>
                      <LockIcon aria-hidden />
                      <span className="sr-only">{t('buataju.locked')}</span>
                    </>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>

      <FeatureLockedModal
        opened={lockedModule !== null}
        onClose={() => setLockedModule(null)}
        moduleLabel={lockedModule ?? undefined}
        returnFocusRef={triggerRef}
      />
    </>
  );
}
